import { usePerms } from 'contexts/PermsContext';
import useSirenAudio from 'hooks/useSirenAudio';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  useDeleteAudioMutation,
  useEditAudioMutation,
  useGetConnectedDevicesQuery,
  useSaveTempAudioMutation,
  useUpdateAudioMutation,
} from 'services/devices/devices.api';
import {
  notifyErrorToast,
  notifySuccessToast,
  resolveMessageText,
} from 'utils/notifications/toast.utils';
import AssignByAudioSection from './components/AssignByAudio';
import AssociationConfirmationModal from './components/AssociationConfirmationModal';
import AudioManagerHeader from './components/AudioManagerHeader';
import DeleteAudioModal from './components/DeleteAudioModal';
import ManageDeviceAssociationsSection from './components/ManageDeviceAssociations';
import useCompactLayout from './hooks/useCompactLayout';
import {
  filterOptions,
  getCurrentAssociatedAudioIdsForDevice,
  getDeviceOptions,
  groupOptionsBySystemAndType,
} from './utils/audioManager.utils';

/**
 * Manages siren audio files and their device associations.
 *
 * @param {object} props - Audio Manager configuration.
 * @returns {JSX.Element|null} The Audio Manager interface for the selected devices.
 */
export default function AudioManager({
  deviceIds,
  group,
  onClose = () => {},
  onPlayTemp = () => {},
}) {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [saveTempAudio] = useSaveTempAudioMutation();
  const [updateAudio] = useUpdateAudioMutation();
  const [deleteAudio] = useDeleteAudioMutation();
  const [editAudio] = useEditAudioMutation();

  const perms = usePerms();
  const containerRef = useRef(null);
  const isCompactLayout = useCompactLayout(containerRef);

  // Keeps the same array reference unless a tracked device's id/type actually
  // changes, so unrelated device updates don't refetch every audio file.
  const deviceIdsKey = (deviceIds || []).join(',');
  const relevantDevices = useMemo(
    () =>
      (connectedDevices || [])
        .filter((device) => deviceIds?.includes(device.id))
        .map((device) => ({ id: device.id, device_type: device.device_type })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deviceIdsKey, connectedDevices],
  );
  const relevantDevicesKey = JSON.stringify(relevantDevices);
  const deviceObjectsRef = useRef({ key: null, value: [] });
  if (deviceObjectsRef.current.key !== relevantDevicesKey) {
    deviceObjectsRef.current = { key: relevantDevicesKey, value: relevantDevices };
  }
  const deviceObjects = deviceObjectsRef.current.value;
  const { allAudio } = useSirenAudio(deviceObjects);

  const [selectedDevices, setSelectedDevices] = useState([]);
  const [file, setFile] = useState(null);
  const [blob, setBlob] = useState(null);
  const [fileName, setFileName] = useState('');
  const [selectedAudios, setSelectedAudios] = useState([]);
  const [managerMode, setManagerMode] = useState('audio');
  const [deviceSearchValue, setDeviceSearchValue] = useState('');
  const [manageDeviceSearchValue, setManageDeviceSearchValue] = useState('');
  const [audioList, setAudioList] = useState([]);
  const [managedDeviceAudioSelections, setManagedDeviceAudioSelections] = useState({});
  const [showAssociationConfirmation, setShowAssociationConfirmation] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [toEdit, setToEdit] = useState(null);
  const [editName, setEditName] = useState('');
  const [updateAudioList, setUpdateAudioList] = useState(false);

  useEffect(() => {
    setAudioList(allAudio.filter((audio) => !!audio.device_ids));
  }, [allAudio]);

  useEffect(() => {
    if (selectedAudios.length > 0 || selectedDevices.length > 0) return;
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) return;
    if (!audioList.length) return;

    const scopedDeviceIds = new Set(deviceIds.map((id) => `${id}`));
    const initiallySelectedAudio = audioList.find(
      (audio) =>
        Array.isArray(audio.device_ids) &&
        audio.device_ids.some((id) => scopedDeviceIds.has(`${id}`)),
    );

    if (!initiallySelectedAudio) return;

    setSelectedAudios([initiallySelectedAudio.id]);
    setSelectedDevices([...(initiallySelectedAudio.device_ids || [])]);
  }, [audioList, deviceIds, selectedAudios.length, selectedDevices.length]);

  const updateFile = (newFile) => {
    setBlob(null);
    setFile(newFile);
  };

  const updateBlob = (newBlob, newFileName) => {
    if (newBlob && file) setFile(null);
    setBlob(newBlob);
    setFileName(newFileName);
  };

  const deviceAssociationOptions = useMemo(
    () => getDeviceOptions(connectedDevices, group),
    [connectedDevices, group],
  );

  const manageDeviceOptions = useMemo(() => {
    if (!Array.isArray(deviceIds) || deviceIds.length === 0) return deviceAssociationOptions;

    const scopedIds = new Set(deviceIds.map((id) => `${id}`));
    const scopedOptions = deviceAssociationOptions.filter((option) =>
      scopedIds.has(`${option.value}`),
    );

    return scopedOptions.length > 0 ? scopedOptions : deviceAssociationOptions;
  }, [deviceAssociationOptions, deviceIds]);

  const filteredDeviceAssociationOptions = useMemo(
    () => filterOptions(deviceAssociationOptions, deviceSearchValue),
    [deviceAssociationOptions, deviceSearchValue],
  );

  const filteredManageDeviceOptions = useMemo(
    () => filterOptions(manageDeviceOptions, manageDeviceSearchValue),
    [manageDeviceOptions, manageDeviceSearchValue],
  );

  const canEditDelete = perms.testPermission([], ['Edit-DeleteSirenAudioFiles']);
  const hasSelectedAudio = selectedAudios.length > 0;
  const canEditDeviceAssociations = hasSelectedAudio && !group;
  const selectedAudioId = selectedAudios[0] ?? null;
  const hasManagedSelectionOverride = (deviceId) =>
    Object.prototype.hasOwnProperty.call(managedDeviceAudioSelections, `${deviceId}`);

  const getEffectiveManagedAudioIdsForDevice = (deviceId) =>
    hasManagedSelectionOverride(deviceId)
      ? managedDeviceAudioSelections[`${deviceId}`] || []
      : getCurrentAssociatedAudioIdsForDevice(audioList, deviceId);

  const groupedDeviceAssociationOptions = useMemo(
    () =>
      groupOptionsBySystemAndType(
        filteredDeviceAssociationOptions,
        (items) => items.filter((device) => selectedDevices.includes(device.value)).length,
      ),
    [filteredDeviceAssociationOptions, selectedDevices],
  );

  const groupedManageDeviceOptions = useMemo(
    () =>
      groupOptionsBySystemAndType(
        filteredManageDeviceOptions,
        (items) =>
          items.filter((device) => getEffectiveManagedAudioIdsForDevice(device.value).length > 0)
            .length,
      ),
    [filteredManageDeviceOptions, managedDeviceAudioSelections, audioList],
  );

  useEffect(() => {
    const availableAudioIds = new Set(audioList.map((audio) => audio.id));
    const availableDeviceIds = new Set(manageDeviceOptions.map((option) => `${option.value}`));

    setManagedDeviceAudioSelections((prev) => {
      const nextSelections = {};
      let changed = false;

      Object.entries(prev).forEach(([deviceKey, selectedAudioIds]) => {
        if (!availableDeviceIds.has(deviceKey)) {
          changed = true;
          return;
        }

        const filteredSelections = selectedAudioIds.filter((audioId) =>
          availableAudioIds.has(audioId),
        );
        nextSelections[deviceKey] = filteredSelections;

        if (
          filteredSelections.length !== selectedAudioIds.length ||
          selectedAudioIds.some((audioId) => !filteredSelections.includes(audioId))
        ) {
          changed = true;
        }
      });

      if (Object.keys(prev).some((key) => !(key in nextSelections))) changed = true;

      return changed ? nextSelections : prev;
    });
  }, [audioList, manageDeviceOptions]);

  const getSelectedAudioCountForDevice = (deviceId) =>
    getEffectiveManagedAudioIdsForDevice(deviceId).length;

  const getManagedAudiosForDevice = (deviceId) => {
    const normalizedDeviceId = `${deviceId}`;
    return audioList.filter(
      (audio) =>
        Array.isArray(audio.device_ids) ||
        getEffectiveManagedAudioIdsForDevice(normalizedDeviceId).includes(audio.id),
    );
  };

  const syncManagedSelectionsForAudio = (audioId, deviceIdsForAudio) => {
    if (!audioId) return;

    const normalizedNextDeviceIds = new Set((deviceIdsForAudio || []).map((id) => `${id}`));

    setManagedDeviceAudioSelections((prev) => {
      const nextSelections = { ...prev };

      manageDeviceOptions.forEach((option) => {
        const deviceKey = `${option.value}`;
        const currentSelections = nextSelections[deviceKey] || [];
        const hasAudioSelected = currentSelections.includes(audioId);
        const shouldIncludeAudio = normalizedNextDeviceIds.has(deviceKey);

        if (shouldIncludeAudio && !hasAudioSelected) {
          nextSelections[deviceKey] = [...currentSelections, audioId];
        } else if (!shouldIncludeAudio && hasAudioSelected) {
          nextSelections[deviceKey] = currentSelections.filter((id) => id !== audioId);
        }
      });

      return nextSelections;
    });
  };

  const toggleAssociatedDevice = (deviceId) => {
    if (!canEditDeviceAssociations) return;
    setSelectedDevices((prev) => {
      const nextSelectedDevices = prev.includes(deviceId)
        ? prev.filter((selectedDeviceId) => selectedDeviceId !== deviceId)
        : [...prev, deviceId];

      syncManagedSelectionsForAudio(selectedAudioId, nextSelectedDevices);
      return nextSelectedDevices;
    });
  };

  const toggleManagedAudioForDevice = (deviceId, audioId) => {
    const normalizedDeviceId = `${deviceId}`;
    setManagedDeviceAudioSelections((prev) => {
      const currentSelections = hasManagedSelectionOverride(normalizedDeviceId)
        ? prev[normalizedDeviceId] || []
        : getCurrentAssociatedAudioIdsForDevice(audioList, normalizedDeviceId);
      const nextSelections = currentSelections.includes(audioId)
        ? currentSelections.filter((id) => id !== audioId)
        : [...currentSelections, audioId];

      return {
        ...prev,
        [normalizedDeviceId]: nextSelections,
      };
    });

    if (selectedAudioId === audioId) {
      setSelectedDevices((prev) => {
        const normalizedDeviceValue = `${deviceId}`;
        const hasDeviceSelected = prev.some((id) => `${id}` === normalizedDeviceValue);

        if (hasDeviceSelected) {
          return prev.filter((id) => `${id}` !== normalizedDeviceValue);
        }

        return [...prev, deviceId];
      });
    }
  };

  const clearManagedAudiosForVisibleDevices = () => {
    const visibleDeviceIds = filteredManageDeviceOptions.map((option) => `${option.value}`);

    setManagedDeviceAudioSelections((prev) => {
      const nextSelections = { ...prev };
      visibleDeviceIds.forEach((deviceId) => {
        nextSelections[deviceId] = [];
      });
      return nextSelections;
    });
  };

  const onPlayTempInt = () => onPlayTemp(fileName);

  const onEdit = (audio) => {
    setToEdit(audio.id);
    setEditName(audio.name);
  };

  const onResolveEdit = async () => {
    if (!toEdit || !editName) return;
    try {
      await editAudio({ audioId: toEdit, fileName: editName }).unwrap();
      setToEdit(null);
      setEditName('');
      notifySuccessToast(editName ? `'${editName}'` : 'Audio file', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    } catch (error) {
      console.error('Error in AudioManager:onResolveEdit:', error);
      notifyErrorToast(editName ? `'${editName}'` : 'Audio file', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    }
  };

  const onCancelEdit = () => {
    setEditName('');
    setToEdit(null);
  };

  const onAudioSelectToggle = (e, audioId) => {
    if (e.target.checked) {
      const audioFound = audioList.find((audio) => audio.id === audioId);
      setSelectedAudios([audioId]);
      const nextSelectedDevices = [...(audioFound?.device_ids || [])];
      setSelectedDevices(nextSelectedDevices);
      syncManagedSelectionsForAudio(audioId, nextSelectedDevices);
    } else {
      setSelectedAudios([]);
      setSelectedDevices([]);
    }
  };

  const onConfirmDelete = async () => {
    if (!toDelete) return;
    const audioToDelete = audioList.find((audio) => audio.id === toDelete);
    try {
      await deleteAudio([toDelete]).unwrap();
      setAudioList((prev) => prev.filter((audio) => audio.id !== toDelete));
      setSelectedAudios((prev) => prev.filter((id) => id !== toDelete));
      setSelectedDevices((prev) => (selectedAudioId === toDelete ? [] : prev));
      notifySuccessToast(audioToDelete?.name ? `'${audioToDelete.name}'` : 'Audio file', {
        isAddOperation: false,
        isDeleteOperation: true,
      });
    } catch (e) {
      console.error('Error in AudioManager:onConfirmDelete:', e);
      notifyErrorToast(audioToDelete?.name ? `'${audioToDelete.name}'` : 'Audio file', {
        isAddOperation: false,
        isDeleteOperation: true,
      });
    } finally {
      setToDelete(null);
    }
  };

  const onSaveTemp = async (toDeleteTemp, override) => {
    try {
      await saveTempAudio({ toDelete: toDeleteTemp || false, override: override }).unwrap();
      setUpdateAudioList((prev) => !prev);
      notifySuccessToast(fileName ? `'${fileName}'` : 'Audio file', {
        isAddOperation: true,
      });
    } catch (e) {
      console.error('Error in AudioManager:onSaveTemp:', e);
      const backendMessage =
        resolveMessageText(e?.response?.data) ||
        resolveMessageText(e?.data) ||
        resolveMessageText(e?.message);
      toast.error(
        backendMessage
          ? `Audio Manager save step failed: ${backendMessage}`
          : 'Audio Manager save step failed.',
      );
    }
  };

  const associationConfirmationSummary = useMemo(() => {
    const getAudioName = (audioId) => {
      const audio = audioList.find((entry) => entry.id === audioId);
      return audio?.displayName || audio?.name || `Audio ${audioId}`;
    };

    const getDeviceName = (deviceId) => {
      const option =
        manageDeviceOptions.find((entry) => `${entry.value}` === `${deviceId}`) ||
        deviceAssociationOptions.find((entry) => `${entry.value}` === `${deviceId}`);
      return option?.label || option?.name || `Device ${deviceId}`;
    };

    if (managerMode === 'device') {
      const deviceNames = [];
      const audioIds = new Set();

      manageDeviceOptions.forEach((device) => {
        const associatedAudioIds = getEffectiveManagedAudioIdsForDevice(device.value);
        if (!associatedAudioIds.length) return;

        deviceNames.push(getDeviceName(device.value));
        associatedAudioIds.forEach((audioId) => audioIds.add(audioId));
      });

      return {
        audioFiles: Array.from(audioIds).map(getAudioName),
        sirenDevices: deviceNames,
      };
    }

    return {
      audioFiles: selectedAudios.map(getAudioName),
      sirenDevices: selectedDevices.map(getDeviceName),
    };
  }, [
    audioList,
    deviceAssociationOptions,
    getEffectiveManagedAudioIdsForDevice,
    manageDeviceOptions,
    managerMode,
    selectedAudios,
    selectedDevices,
  ]);

  const onConfirmAssociations = async () => {
    try {
      if (managerMode === 'device') {
        if (manageDeviceOptions.length === 0) {
          onClose();
          return;
        }

        const scopedManagedDeviceIds = manageDeviceOptions.map((option) => `${option.value}`);
        const updates = audioList
          .map((audio) => {
            const currentDeviceIds = Array.isArray(audio.device_ids)
              ? audio.device_ids.map((id) => `${id}`)
              : [];
            const nextDeviceIds = currentDeviceIds.filter(
              (deviceId) => !scopedManagedDeviceIds.includes(`${deviceId}`),
            );

            scopedManagedDeviceIds.forEach((deviceId) => {
              const selectedAudioIds = managedDeviceAudioSelections[deviceId] || [];
              if (selectedAudioIds.includes(audio.id)) nextDeviceIds.push(deviceId);
            });

            const normalizedNextDeviceIds = [...new Set(nextDeviceIds.map((id) => `${id}`))];
            const isUnchanged =
              currentDeviceIds.length === normalizedNextDeviceIds.length &&
              currentDeviceIds.every((id) => normalizedNextDeviceIds.includes(`${id}`));

            if (isUnchanged) return null;

            return updateAudio({
              audioId: audio.id,
              deviceIds: normalizedNextDeviceIds,
            }).unwrap();
          })
          .filter(Boolean);

        await Promise.all(updates);
        setAudioList((prev) =>
          prev.map((audio) => {
            const currentDeviceIds = Array.isArray(audio.device_ids)
              ? audio.device_ids.map((id) => `${id}`)
              : [];
            const scopedManagedDeviceIds = manageDeviceOptions.map((option) => `${option.value}`);
            const nextDeviceIds = currentDeviceIds.filter(
              (deviceId) => !scopedManagedDeviceIds.includes(`${deviceId}`),
            );

            scopedManagedDeviceIds.forEach((deviceId) => {
              const selectedAudioIds = managedDeviceAudioSelections[deviceId] || [];
              if (selectedAudioIds.includes(audio.id)) nextDeviceIds.push(deviceId);
            });

            return {
              ...audio,
              device_ids: [...new Set(nextDeviceIds.map((id) => `${id}`))],
            };
          }),
        );

        if (selectedAudioId) {
          const selectedAudioDeviceIds = manageDeviceOptions
            .filter((option) =>
              (managedDeviceAudioSelections[`${option.value}`] || []).includes(selectedAudioId),
            )
            .map((option) => option.value);
          setSelectedDevices(selectedAudioDeviceIds);
        }

        notifySuccessToast('Device audio associations', {
          isAddOperation: false,
          isDeleteOperation: false,
        });
        setShowAssociationConfirmation(false);
        onClose();
        return;
      }

      if (!selectedAudios || selectedAudios.length < 1) {
        onClose();
        return;
      }

      const audioToUpdate = Array.isArray(selectedAudios) ? selectedAudios[0] : selectedAudios;
      let devicesToUpdate = group ? [...deviceIds] : [...selectedDevices];

      if (group) {
        const audioFound = audioList.find((audio) => audio.id === audioToUpdate);
        if (audioFound) {
          devicesToUpdate = [...new Set([...devicesToUpdate, ...audioFound.device_ids])];
        }
      }

      await updateAudio({ audioId: audioToUpdate, deviceIds: devicesToUpdate }).unwrap();
      setAudioList((prev) =>
        prev.map((audio) =>
          audio.id === audioToUpdate ? { ...audio, device_ids: [...devicesToUpdate] } : audio,
        ),
      );
      syncManagedSelectionsForAudio(audioToUpdate, devicesToUpdate);
      notifySuccessToast('Audio association', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
      setShowAssociationConfirmation(false);
      onClose();
    } catch (e) {
      console.error('Error in AudioManager:onDone:', e);
      notifyErrorToast('Audio association', {
        isAddOperation: false,
        isDeleteOperation: false,
      });
    }
  };

  const onDone = () => {
    setShowAssociationConfirmation(true);
  };

  const sectionCardClass = `d-flex flex-column min-h-0 overflow-hidden rounded-2 border border-secondary border-opacity-50 bg-primary-shadow ${
    isCompactLayout ? 'flex-shrink-0' : 'flex-fill'
  }`;

  if (!deviceIds || deviceIds?.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className="d-flex flex-column gap-2 px-3 pb-3 pt-2 h-100 min-h-0 overflow-hidden"
    >
      <AudioManagerHeader managerMode={managerMode} onModeChange={setManagerMode} />

      <div className="d-flex flex-column flex-grow-1 min-h-0 overflow-y-auto overflow-x-hidden pe-1 customScrollbar-Y gap-3">
        <div
          hidden={managerMode !== 'audio'}
          className={
            managerMode === 'audio' ? 'd-flex flex-column flex-grow-1 min-h-0 gap-3' : 'd-none'
          }
        >
          <AssignByAudioSection
            sectionCardClass={sectionCardClass}
            isCompactLayout={isCompactLayout}
            file={file}
            audioList={audioList}
            blob={blob}
            updateFile={updateFile}
            updateBlob={updateBlob}
            onSaveTemp={onSaveTemp}
            onPlayTemp={onPlayTempInt}
            selectedAudios={selectedAudios}
            canEditDelete={canEditDelete}
            onAudioSelectToggle={onAudioSelectToggle}
            toEdit={toEdit}
            editName={editName}
            setEditName={setEditName}
            onEdit={onEdit}
            onResolveEdit={onResolveEdit}
            onCancelEdit={onCancelEdit}
            setToDelete={setToDelete}
            updateAudioList={updateAudioList}
            deviceSearchValue={deviceSearchValue}
            setDeviceSearchValue={setDeviceSearchValue}
            hasSelectedAudio={hasSelectedAudio}
            groupedDeviceAssociationOptions={groupedDeviceAssociationOptions}
            selectedDevices={selectedDevices}
            canEditDeviceAssociations={canEditDeviceAssociations}
            onToggleAssociatedDevice={toggleAssociatedDevice}
          />
        </div>
        <div
          hidden={managerMode !== 'device'}
          className={
            managerMode === 'device' ? 'd-flex flex-column flex-grow-1 min-h-0 gap-3' : 'd-none'
          }
        >
          <ManageDeviceAssociationsSection
            sectionCardClass={sectionCardClass}
            isCompactLayout={isCompactLayout}
            manageDeviceSearchValue={manageDeviceSearchValue}
            setManageDeviceSearchValue={setManageDeviceSearchValue}
            filteredManageDeviceOptions={filteredManageDeviceOptions}
            clearManagedAudiosForVisibleDevices={clearManagedAudiosForVisibleDevices}
            groupedManageDeviceOptions={groupedManageDeviceOptions}
            managedDeviceAudioSelections={managedDeviceAudioSelections}
            getEffectiveManagedAudioIdsForDevice={getEffectiveManagedAudioIdsForDevice}
            getSelectedAudioCountForDevice={getSelectedAudioCountForDevice}
            getManagedAudiosForDevice={getManagedAudiosForDevice}
            toggleManagedAudioForDevice={toggleManagedAudioForDevice}
            toEdit={toEdit}
            editName={editName}
            setEditName={setEditName}
            onEdit={onEdit}
            onResolveEdit={onResolveEdit}
            onCancelEdit={onCancelEdit}
            setToDelete={setToDelete}
            canEditDelete={canEditDelete}
            updateAudioList={updateAudioList}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center flex-shrink-0 pt-1">
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onDone}
          disabled={
            (managerMode === 'audio' && selectedAudios.length < 1) ||
            (managerMode === 'device' && manageDeviceOptions.length < 1)
          }
        >
          Done
        </Button>
      </div>

      <DeleteAudioModal
        show={toDelete !== null}
        audioName={
          audioList.find((audio) => audio.id === toDelete)?.displayName ||
          audioList.find((audio) => audio.id === toDelete)?.name ||
          'This file'
        }
        onCancel={() => setToDelete(null)}
        onConfirm={onConfirmDelete}
      />
      <AssociationConfirmationModal
        show={showAssociationConfirmation}
        audioFiles={associationConfirmationSummary.audioFiles}
        sirenDevices={associationConfirmationSummary.sirenDevices}
        onCancel={() => setShowAssociationConfirmation(false)}
        onConfirm={onConfirmAssociations}
      />
    </div>
  );
}
